import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, AlertTriangle, BookLock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { BackButton } from "@/components/Common";
import { TZ } from "@/pages/Oggi";

type Zone = { id: string; label: string; shapes: React.ReactNode };
const R = (x: number, y: number, w: number, h: number, r: number) => <rect x={x} y={y} width={w} height={h} rx={r} />;
const ARMS = <>{R(30, 96, 26, 150, 13)}{R(144, 96, 26, 150, 13)}</>;
const LEGS = <>{R(68, 216, 30, 200, 15)}{R(102, 216, 30, 200, 15)}</>;
const FRONT: Zone[] = [
  { id: "testa", label: "Testa", shapes: <ellipse cx={100} cy={40} rx={27} ry={31} /> },
  { id: "fronte_occhi", label: "Fronte e occhi", shapes: R(80, 24, 40, 24, 12) },
  { id: "gola", label: "Gola", shapes: R(86, 72, 28, 18, 9) },
  { id: "petto", label: "Petto", shapes: R(60, 92, 80, 60, 24) },
  { id: "addome", label: "Addome", shapes: R(64, 154, 72, 58, 22) },
  { id: "braccia_mani", label: "Braccia e mani", shapes: ARMS },
  { id: "gambe_piedi", label: "Gambe e piedi", shapes: LEGS },
];
const BACK: Zone[] = [
  { id: "nuca", label: "Testa (dietro)", shapes: <ellipse cx={100} cy={40} rx={27} ry={31} /> },
  { id: "collo", label: "Collo", shapes: R(86, 72, 28, 18, 9) },
  { id: "schiena_alta", label: "Schiena alta", shapes: R(60, 92, 80, 60, 24) },
  { id: "schiena_bassa", label: "Schiena bassa", shapes: R(64, 154, 72, 58, 22) },
  { id: "braccia_mani", label: "Braccia e mani", shapes: ARMS },
  { id: "gambe_piedi", label: "Gambe e piedi", shapes: LEGS },
];
const SENS: [string, string][] = [["pressione", "Pressione"], ["dolore", "Dolore"], ["formicolio", "Formicolio"], ["tensione", "Tensione"], ["tremore", "Tremore"], ["palpitazioni", "Palpitazioni"], ["calore", "Calore"], ["altro", "Altro"]];
const STARTED: [string, string][] = [["adesso", "Adesso"], ["oggi", "Oggi"], ["giorni", "Da qualche giorno"], ["settimane", "Da settimane"]];
const YN: [string, string][] = [["si", "Sì"], ["no", "No"], ["non_so", "Non so"]];

function Figure({ zones, sel, onPick, view }: any) {
  return (
    <svg viewBox="0 0 200 430" className="w-full max-w-[240px] mx-auto h-auto" role="group" aria-label={view === "front" ? "Corpo, vista frontale" : "Corpo, vista posteriore"} data-testid={`body-figure-${view}`}>
      {zones.map((z: Zone) => {
        const on = sel === z.id;
        return (
          <g key={z.id} role="button" tabIndex={0} aria-label={z.label} aria-pressed={on} data-testid={`zone-${z.id}`} onClick={() => onPick(z.id)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onPick(z.id))}
            className="cursor-pointer outline-none" fill={on ? "hsl(170 30% 27% / .38)" : "rgba(255,255,255,.55)"} stroke={on ? "hsl(170 30% 27%)" : "rgba(255,255,255,.95)"} strokeWidth={on ? 2.5 : 1.5}>
            {z.shapes}
          </g>
        );
      })}
    </svg>
  );
}

function Pills({ options, value, onPick, multi = false, id }: any) {
  const isOn = (k: string) => (multi ? value.includes(k) : value === k);
  return (
    <div className="flex flex-wrap gap-2" data-testid={id}>
      {options.map(([k, l]: string[]) => (
        <button key={k} type="button" aria-pressed={isOn(k)} onClick={() => onPick(k)} data-testid={`${id}-${k}`}
          className={`chip min-h-[40px] px-4 text-sm ${isOn(k) ? "chip-violet" : "hover:bg-white/70"}`}>{isOn(k) && <Check size={13} />}{l}</button>
      ))}
    </div>
  );
}

function urgent(zone: string, s: string[], intensity: number) {
  const core = ["petto", "gola", "testa", "fronte_occhi", "schiena_alta"].includes(zone);
  return (core && s.some((x) => ["dolore", "pressione", "palpitazioni"].includes(x))) || intensity >= 8;
}

const EMPTY = { sensations: [] as string[], other: "", intensity: 4, started: "adesso", happened_before: "non_so", anxious_now: "non_so", text: "" };

function SensationForm({ zone, view, label, onSaved }: any) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const toggle = (k: string) => setF((x) => ({ ...x, sensations: x.sensations.includes(k) ? x.sensations.filter((s) => s !== k) : [...x.sensations, k] }));
  const save = async () => {
    setBusy(true);
    try {
      const { text, ...b } = f;
      await api.post("/journal", { tz: TZ, kind: "body", text, body: { view, zone, ...b } });
      ["journal", "score"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success("Salvato nel tuo diario privato");
      setF(EMPTY); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="surface p-5 space-y-5" data-testid="sensation-form">
      <h2 className="text-lg font-semibold">{label}: cosa senti?</h2>
      <Pills options={SENS} value={f.sensations} onPick={toggle} multi id="sensation" />
      {f.sensations.includes("altro") && <input className="field" maxLength={120} placeholder="Descrivi con parole tue" value={f.other} onChange={(e) => setF({ ...f, other: e.target.value })} data-testid="sensation-other-input" />}
      <label className="block">
        <span className="text-sm font-medium text-ink/75">Intensità: <b data-testid="intensity-value">{f.intensity}</b> su 10</span>
        <input type="range" min={1} max={10} value={f.intensity} onChange={(e) => setF({ ...f, intensity: +e.target.value })} className="w-full mt-2 accent-[hsl(170_30%_27%)] h-8" data-testid="intensity-input" />
      </label>
      <div className="space-y-2"><span className="text-sm font-medium text-ink/75">Da quando?</span><Pills options={STARTED} value={f.started} onPick={(k: string) => setF({ ...f, started: k })} id="started" /></div>
      <div className="space-y-2"><span className="text-sm font-medium text-ink/75">Ti è già successo?</span><Pills options={YN} value={f.happened_before} onPick={(k: string) => setF({ ...f, happened_before: k })} id="before" /></div>
      <div className="space-y-2"><span className="text-sm font-medium text-ink/75">Senti ansia o paura in questo momento?</span><Pills options={YN} value={f.anxious_now} onPick={(k: string) => setF({ ...f, anxious_now: k })} id="anxious" /></div>
      <textarea className="field min-h-[84px]" maxLength={4000} placeholder="Se vuoi, racconta cosa stava succedendo (facoltativo)" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="sensation-note-input" />
      {urgent(zone, f.sensations, f.intensity) && (
        <div className="rounded-2xl p-4 bg-[hsl(2_60%_96%/.9)] border border-[hsl(2_52%_44%/.35)] text-sm flex gap-3" role="alert" data-testid="urgent-warning">
          <AlertTriangle size={18} className="text-[hsl(var(--destructive))] shrink-0 mt-0.5" />
          <p>Se il dolore o la pressione è forte o nuova, si allarga a braccio, mascella o schiena, oppure hai fatica a respirare, sudore freddo, svenimento, debolezza di un lato del corpo o difficoltà a parlare: <a href="tel:112" className="font-bold underline" data-testid="urgent-call-112">chiama subito il 112</a>. Non dare per scontato che sia ansia.</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" disabled={busy || !f.sensations.length} onClick={save} data-testid="sensation-save-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <BookLock size={16} />}Salva nel diario privato</button>
        <button className="btn btn-ghost" onClick={() => { setF(EMPTY); onSaved(); }} data-testid="sensation-cancel-btn">Non salvare</button>
      </div>
    </section>
  );
}

export default function Corpo() {
  const [view, setView] = useState<"front" | "back">("front");
  const [zone, setZone] = useState<string | null>(null);
  const zones = view === "front" ? FRONT : BACK;
  const current = zones.find((z) => z.id === zone);
  useEffect(() => {
    if (zone) document.querySelector("[data-testid=sensation-form]")?.scrollIntoView({ block: "start" });
  }, [zone]);
  return (
    <div className="px-5 md:px-12 pt-6 md:pt-12 pb-12 max-w-2xl mx-auto space-y-6" data-testid="corpo-page">
      <BackButton parent="/" />
      <header>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Ascolta il tuo corpo</h1>
        <p className="text-ink/70 mt-2">Tocca la zona in cui senti qualcosa. Serve a descrivere e ricordare, non a trovare una causa.</p>
      </header>
      <div className="surface p-5 space-y-4">
        <div className="seg w-fit mx-auto" role="tablist">
          {([["front", "Davanti"], ["back", "Dietro"]] as const).map(([k, l]) => <button key={k} role="tab" aria-selected={view === k} onClick={() => { setView(k); setZone(null); }} className={`seg-item ${view === k ? "seg-active" : ""}`} data-testid={`body-view-${k}`}>{l}</button>)}
        </div>
        <Figure zones={zones} sel={zone} onPick={setZone} view={view} />
        <div className="flex flex-wrap justify-center gap-2" data-testid="zone-list">
          {zones.map((z) => <button key={z.id} onClick={() => setZone(z.id)} aria-pressed={zone === z.id} className={`chip min-h-[36px] ${zone === z.id ? "chip-violet" : "hover:bg-white/70"}`} data-testid={`zone-chip-${z.id}`}>{z.label}</button>)}
        </div>
      </div>
      {current && <SensationForm key={`${view}-${current.id}`} zone={current.id} view={view} label={current.label} onSaved={() => setZone(null)} />}
      <p className="text-xs text-ink/60">Le voci restano nel tuo diario privato: nessun altro può vederle. Se una sensazione è nuova, forte o non passa, parlane con un medico. <Link to="/diario" className="underline" data-testid="corpo-diary-link">Apri il diario</Link></p>
    </div>
  );
}
